import { useCallback, useState } from 'react';
import { BigNumber, ethers } from 'ethers';
import { useAccount, useNetwork } from 'wagmi';
import { Address, parseUnits } from 'viem';

import { getEtherFormatValue } from '../utils';
import { useL1Withdraw } from './useL1Withdraw';
import * as env from '../env';
import { getPublicClientL1 } from '@/utils/clients';
import { standardBridgeAbi } from '@/base/standardBridgeAbi';

export const useGetGasPrice = () => {
  const { crossChainMessenger: c1 } = useL1Withdraw();
  const { chain: currentChain } = useNetwork();
  const { address, connector } = useAccount();
  const [loadingGasLimit, setLoadingGasLimit] = useState(false);
  const [isLoadingGasPrice, setIsLoadingGasPrice] = useState(false);

  const getEstimateGas = useCallback(
    async (tokeNumber: string, type: string) => {
      if (c1) {
        setLoadingGasLimit(true);
        let estiGas;
        try {
          if (type === 'deposit' && currentChain && currentChain.id === Number(env.L1_CHAIN_ID)) {
            const provider = await connector?.getProvider();
            const publicClientL1 = getPublicClientL1({ provider });
            estiGas = await publicClientL1.estimateContractGas({
              address: env.L1StandardBridge as Address,
              abi: standardBridgeAbi,
              functionName: 'depositETHTo',
              args: [address as Address, 100000, '0x'],
              value: parseUnits(tokeNumber, 18),
              account: address as Address,
            } as any);

            // estiGas = await c1.estimateGas.depositETH(
            //   ethers.utils.parseUnits(tokeNumber, 'ether'),
            //   {
            //     l2GasLimit: 1,
            //     overrides: {
            //       from: address,
            //     },
            //   },
            // );
          }
          return estiGas
            ? { val: await getEtherFormatValue(BigNumber.from(estiGas)), error: false, msg: '' }
            : {
                val: await getEtherFormatValue(BigNumber.from('0')),
                error: true,
                msg: 'Unable to get estimated gas from op SDK',
              };
        } catch (e: any) {
          //eslint-disable-next-line no-console
          console.log(e);
          const bn0 = BigNumber.from('0');
          return { val: await getEtherFormatValue(bn0), error: true, msg: e?.code };
        } finally {
          setLoadingGasLimit(false);
        }
      }
    },
    [address, c1, currentChain, connector],
  );

  const getEstimateApproveGas = async (
    l1Address: string,
    l2Address: string,
    tokeNumber: string,
  ) => {
    if (c1 && l1Address && l2Address && address) {
      // approve ERC20 deposit estimated gas
      setLoadingGasLimit(true);
      try {
        const appEstiGas = await c1.estimateGas.approveERC20(
          l1Address,
          l2Address,
          ethers.utils.parseUnits(tokeNumber, 'ether'),
          { overrides: { from: address } },
        );
        // console.log('approve estimate gas sdk output ', appEstiGas);
        return { val: await getEtherFormatValue(appEstiGas), error: false, msg: '' };
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.log(e);
        const bn = BigNumber.from('0');
        return { val: await getEtherFormatValue(bn), error: true, msg: e?.code };
      } finally {
        setLoadingGasLimit(false);
      }
    }
  };

  const getEstimateERC20Gas = async (
    l1Address: string,
    l2Address: string,
    amount: string,
    type: string,
  ) => {
    if (c1 && l1Address && l2Address && address) {
      setLoadingGasLimit(true);
      let estiGas;
      try {
        if (type === 'deposit') {
          const minGasLimit = 100000;
          const provider = await connector?.getProvider();
          const publicClientL1 = getPublicClientL1({ provider });
          estiGas = await publicClientL1.estimateContractGas({
            address: env.L1StandardBridge as Address,
            abi: standardBridgeAbi,
            functionName: 'depositERC20To',
            args: [
              l1Address as Address,
              l2Address as Address,
              address as Address,
              parseUnits(amount, 18),
              minGasLimit,
              '0x',
            ],
            account: address as Address,
          });
        } else {
          estiGas = await c1.estimateGas.withdrawERC20(
            l1Address,
            l2Address,
            ethers.utils.parseUnits(amount, 'ether'),
            { overrides: { from: address } },
          );
        }
        return { val: await getEtherFormatValue(BigNumber.from(estiGas)), error: false, msg: '' };
      } catch (e: any) {
        //eslint-disable-next-line no-console
        console.log(e);
        const bn = BigNumber.from('0');
        return { val: await getEtherFormatValue(bn), error: true, msg: e.message };
      } finally {
        setLoadingGasLimit(false);
      }
    }
  };

  const getGasPrice = async (chainRpc: string) => {
    const l1provider = new ethers.providers.JsonRpcProvider(chainRpc);
    setIsLoadingGasPrice(true);
    try {
      const gasPriceBignumber = await l1provider.getGasPrice();
      const gasPrice = await getEtherFormatValue(gasPriceBignumber);
      setIsLoadingGasPrice(false);
      return gasPrice;
    } catch (e: any) {
      return { val: 0, error: true, msg: e };
    }
  };

  return {
    getEstimateGas, // for deposit
    getEstimateERC20Gas,
    getEstimateApproveGas,
    isLoadingPrice: loadingGasLimit, // Estimated gas limit loading
    getGasPrice,
    isLoadingGasPrice, // L1 or L2 gas price loading
  };
};
